import { Validators } from '@angular/forms';
import {
  ActionTrackerCardWidgetComponent,
  ActionTrackerIssuesWidgetComponent,
  ActivityCommentImageDisplayWidgetComponent,
  ActivityDurationsWidgetComponent,
  BigNumberWidgetComponent,
  CategoryStatusWidgetComponent,
  CheckInLogsWidgetComponent,
  CommentsWidgetComponent,
  CurrentActivityWidgetComponent,
  DaysSinceLastAccidentWidgetComponent,
  DfQrCodeWidgetComponent,
  EmbeddedUrlViewerWidgetComponent,
  EWidget,
  FormIoWidgetComponent,
  ImageViewerWidgetComponent,
  KanbanViewWidgetComponent,
  KpiComparisonWidgetComponent,
  LaborEfficiencyWidgetComponent,
  LineOverviewWidgetComponent,
  LongestTaskDurationsWidgetComponent,
  MetabaseWidgetComponent,
  NavigationWidgetComponent,
  NextFiveWorkOrdersWidgetComponent,
  NoteWidgetComponent,
  ProductionChartWidgetComponent,
  ProductionReviewWidgetComponent,
  ProductionStatusWidgetComponent,
  QrCodeWidgetComponent,
  RatioWidgetComponent,
  SensorHealthStatusWidgetComponent,
  SqcdpMonthlyWidgetComponent,
  TableViewWidgetComponent,
  TemperatureAndHumiditySensorDataWidgetComponent,
  ThroughputTimePerformanceGanttWidgetComponent,
  ThroughputTimePerformanceKpiWidgetComponent,
  ThroughputTimePerformanceTableWidgetComponent,
  TrendComparisonWidgetComponent,
  TWidgetCustomParameters,
  WoCountsWidgetComponent,
  WoOeeWidgetComponent,
  WorkInstructionsWidgetComponent,
  WoSpeedWidgetComponent,
} from 'scw-digital-boards-widgets';
import { IsUrlValidator } from '../../core/validators/is-url.validator';
import { TrimmedRequiredValidator } from '../../core/validators/trimmed-required.validator';
import { IDropdownOption } from '../../shared/components/mat-dropdown/mat-dropdown.component';
import {
  EDashboardLayout,
  EWidgetInputType,
  ITypeBasedConfigurationInput,
  TCustomParameterKeyName,
} from './dashboard-content.interface';
import { NOTE_MAX_LENGTH } from './edit-note-dialog/edit-note-dialog.component';
import { ETypeDependentInputs, IWidgetBasedConstant } from './widget-add-edit-dialog/widget-add-edit-dialog.interface';

export const addWidgetConfiguration: Record<EWidget, IWidgetBasedConstant> = {
  [EWidget.actionTrackerCard]: {
    component: ActionTrackerCardWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 18, w: 2 },
      [EDashboardLayout.fitToScreen]: { h: 3, w: 2 },
    },
    descriptionTranslateKey: 'widget.actionTrackerCard.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['actionTracker'],
    visibleInputsInOrder: [
      { type: 'filterSection' },
      {
        rules: [],
        type: ETypeDependentInputs.escalationStatusSingleSelectDropdown,
      },
      { rules: [], type: ETypeDependentInputs.endpointParameterMultiSelectDropdown },
    ],
  },
  [EWidget.actionTrackerIssues]: {
    component: ActionTrackerIssuesWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 20, w: 4 },
      [EDashboardLayout.fitToScreen]: { h: 3, w: 4 },
    },
    descriptionTranslateKey: 'widget.actionTrackerIssues.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['actionTracker'],
    visibleInputsInOrder: [
      { type: 'filterSection' },
      {
        rules: [],
        type: ETypeDependentInputs.escalationStatusSingleSelectDropdown,
      },
      { rules: [], type: ETypeDependentInputs.endpointParameterMultiSelectDropdown },
      {
        rules: [],
        type: ETypeDependentInputs.sortBySingleSelectDropdown,
      },
      {
        rules: [],
        type: ETypeDependentInputs.datatableConfiguration,
      },
    ],
  },
  [EWidget.activityCommentImageDisplay]: {
    component: ActivityCommentImageDisplayWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 30, w: 3 },
      [EDashboardLayout.fitToScreen]: { h: 4, w: 3 },
    },
    descriptionTranslateKey: 'widget.activityCommentImageDisplay.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [
      { type: 'filterSection' },
      { rules: [Validators.required], type: ETypeDependentInputs.showThumbnailCheckbox },
      { rules: [Validators.max(3600), Validators.min(1)], type: ETypeDependentInputs.displayIntervalInput },
    ],
  },
  [EWidget.activityDurations]: {
    component: ActivityDurationsWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 30, w: 4 },
      [EDashboardLayout.fitToScreen]: { h: 4, w: 4 },
    },
    descriptionTranslateKey: 'widget.activityDurations.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [{ type: 'filterSection' }],
  },
  [EWidget.bigNumber]: {
    component: BigNumberWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 24, w: 2 },
      [EDashboardLayout.fitToScreen]: { h: 3, w: 2 },
    },
    descriptionTranslateKey: 'widget.bigNumber.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [
      { rules: [Validators.required], type: ETypeDependentInputs.widgetRequestCategorySingleSelectDropdown },
      { rules: [Validators.required], type: ETypeDependentInputs.widgetRequestSingleSelectDropdown },
      { rules: [Validators.required], type: ETypeDependentInputs.formDataType },
      { rules: [Validators.required], type: ETypeDependentInputs.formField },
      { rules: [Validators.required], type: ETypeDependentInputs.resultAggregation },
      { rules: [], type: ETypeDependentInputs.endpointParameterMultiSelectDropdown },
      { type: 'filterSection' },
      { rules: [], type: ETypeDependentInputs.thresholdBasedColoringSelection },
    ],
  },
  [EWidget.checkInLogs]: {
    component: CheckInLogsWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 13, w: 12 },
      [EDashboardLayout.fitToScreen]: { h: 2, w: 12 },
    },
    descriptionTranslateKey: 'widget.checkInLogs.description',
    refreshIntervalOptions: null,
    relatedModules: ['laborTracker'],
    visibleInputsInOrder: [
      { type: 'filterSection' },
      { rules: [], type: ETypeDependentInputs.checkInLogsGroupingSingleSelectDropdown },
      { rules: [], type: ETypeDependentInputs.checkInLogsDisplayConfigurationMultiSelectDropdown },
    ],
  },
  [EWidget.currentActivity]: {
    component: CurrentActivityWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 24, w: 2 },
      [EDashboardLayout.fitToScreen]: { h: 4, w: 2 },
    },
    descriptionTranslateKey: 'widget.currentActivity.description',
    refreshIntervalOptions: null,
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [{ type: 'filterSection' }],
  },
  [EWidget.daysSinceLastAccident]: {
    component: DaysSinceLastAccidentWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 33, w: 2 },
      [EDashboardLayout.fitToScreen]: { h: 4, w: 2 },
    },
    descriptionTranslateKey: 'widget.daysSinceLastAccident.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['actionTracker'],
    visibleInputsInOrder: [
      { type: 'filterSection' },
      { rules: [], type: ETypeDependentInputs.endpointParameterMultiSelectDropdown },
    ],
  },
  [EWidget.dfQrCode]: {
    component: DfQrCodeWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 23, w: 2 },
      [EDashboardLayout.fitToScreen]: { h: 3, w: 2 },
    },
    descriptionTranslateKey: 'widget.dfQrCode.description',
    refreshIntervalOptions: null,
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [
      { type: 'filterSection' },
      {
        rules: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(50)],
        type: ETypeDependentInputs.text,
      },
    ],
  },
  [EWidget.embeddedURLViewer]: {
    component: EmbeddedUrlViewerWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 42, w: 2 },
      [EDashboardLayout.fitToScreen]: { h: 5, w: 2 },
    },
    descriptionTranslateKey: 'widget.embeddedURLViewer.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['utilityWidgets'],
    visibleInputsInOrder: [{ rules: [Validators.required], type: ETypeDependentInputs.url }],
  },
  [EWidget.formIo]: {
    component: FormIoWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 55, w: 3 },
      [EDashboardLayout.fitToScreen]: { h: 6, w: 3 },
    },
    descriptionTranslateKey: 'widget.formIo.description',
    refreshIntervalOptions: null,
    relatedModules: ['digitalForms'],
    visibleInputsInOrder: [
      { rules: [Validators.required], type: ETypeDependentInputs.formSingleSelectDropdown },
      { rules: [Validators.required], type: ETypeDependentInputs.formAssociation },
      { rules: [], type: ETypeDependentInputs.formAutofillSingleSelectDropdown },
    ],
  },
  [EWidget.kpiComparison]: {
    component: KpiComparisonWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 42, w: 4 },
      [EDashboardLayout.fitToScreen]: { h: 5, w: 4 },
    },
    descriptionTranslateKey: 'widget.kpiComparison.description',
    refreshIntervalOptions: null,
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [
      { type: 'filterSection' },
      { rules: [Validators.required], type: ETypeDependentInputs.comparisonSourceSelectDropdown },
      { rules: [Validators.required], type: ETypeDependentInputs.fromLastNDays },
    ],
  },
  [EWidget.laborEfficiency]: {
    component: LaborEfficiencyWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 24, w: 5 },
      [EDashboardLayout.fitToScreen]: { h: 3, w: 5 },
    },
    descriptionTranslateKey: 'widget.laborEfficiency.description',
    refreshIntervalOptions: [900, 1800, 2700, 3600],
    relatedModules: ['laborTracker'],
    visibleInputsInOrder: [
      { type: 'filterSection' },
      { rules: [Validators.required], type: ETypeDependentInputs.laborEfficiencyUnitDropdown },
      { rules: [Validators.required], type: ETypeDependentInputs.laborEfficiencyModeDropdown },
    ],
  },
  [EWidget.lineOverview]: {
    component: LineOverviewWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 8, w: 12 },
      [EDashboardLayout.fitToScreen]: { h: 1, w: 12 },
    },
    descriptionTranslateKey: 'widget.lineOverview.description',
    refreshIntervalOptions: null,
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [
      { type: 'filterSection' },
      {
        rules: [],
        type: ETypeDependentInputs.datatableConfiguration,
      },
    ],
  },
  [EWidget.longestTaskDurations]: {
    component: LongestTaskDurationsWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 43, w: 4 },
      [EDashboardLayout.fitToScreen]: { h: 5, w: 4 },
    },
    descriptionTranslateKey: 'widget.longestTaskDurations.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [{ type: 'filterSection' }],
  },
  [EWidget.navigation]: {
    component: NavigationWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 6, w: 4 },
      [EDashboardLayout.fitToScreen]: { h: 1, w: 4 },
    },

    descriptionTranslateKey: 'widget.navigation.description',
    refreshIntervalOptions: null,
    relatedModules: ['utilityWidgets'],
    visibleInputsInOrder: [
      {
        rules: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(50)],
        type: ETypeDependentInputs.text,
      },
      { rules: [Validators.required, TrimmedRequiredValidator(), IsUrlValidator()], type: ETypeDependentInputs.text2 },
    ],
  },
  [EWidget.nextFiveWorkOrder]: {
    component: NextFiveWorkOrdersWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 52, w: 3 },
      [EDashboardLayout.fitToScreen]: { h: 6, w: 3 },
    },
    descriptionTranslateKey: 'widget.nextFiveWorkOrder.description',
    refreshIntervalOptions: null,
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [
      { type: 'filterSection' },
      {
        rules: [Validators.required],
        type: ETypeDependentInputs.nextFiveWorkOrderCountSingleSelectDropdown,
      },
      {
        rules: [Validators.required],
        type: ETypeDependentInputs.nextFiveWorkOrderListFieldOneSingleSelectDropdown,
      },
      {
        rules: [Validators.required],
        type: ETypeDependentInputs.nextFiveWorkOrderListFieldTwoSingleSelectDropdown,
      },
      {
        rules: [Validators.required],
        type: ETypeDependentInputs.nextFiveWorkOrderListFieldThreeSingleSelectDropdown,
      },
      {
        rules: [Validators.required],
        type: ETypeDependentInputs.nextFiveWorkOrderListFieldFourSingleSelectDropdown,
      },
    ],
  },
  [EWidget.note]: {
    component: NoteWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 20, w: 2 },
      [EDashboardLayout.fitToScreen]: { h: 3, w: 2 },
    },
    descriptionTranslateKey: 'widget.note.description',
    refreshIntervalOptions: null,
    relatedModules: ['utilityWidgets'],
    visibleInputsInOrder: [
      {
        rules: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(NOTE_MAX_LENGTH)],
        type: ETypeDependentInputs.text,
      },
    ],
  },
  [EWidget.productionChart]: {
    component: ProductionChartWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 42, w: 5 },
      [EDashboardLayout.fitToScreen]: { h: 5, w: 5 },
    },
    descriptionTranslateKey: 'widget.productionChart.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [{ type: 'filterSection' }],
  },
  [EWidget.productionStatus]: {
    component: ProductionStatusWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 20, w: 4 },
      [EDashboardLayout.fitToScreen]: { h: 3, w: 4 },
    },
    descriptionTranslateKey: 'widget.productionStatus.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [{ type: 'filterSection' }],
  },
  [EWidget.qrCode]: {
    component: QrCodeWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 23, w: 2 },
      [EDashboardLayout.fitToScreen]: { h: 3, w: 2 },
    },
    descriptionTranslateKey: 'widget.qrCode.description',
    refreshIntervalOptions: null,
    relatedModules: ['utilityWidgets'],
    visibleInputsInOrder: [
      {
        rules: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(255)],
        type: ETypeDependentInputs.text,
      },
    ],
  },
  [EWidget.ratio]: {
    component: RatioWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 24, w: 2 },
      [EDashboardLayout.fitToScreen]: { h: 3, w: 2 },
    },
    descriptionTranslateKey: 'widget.ratio.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [
      { rules: [Validators.required], type: ETypeDependentInputs.widgetRequestCategorySingleSelectDropdown },
      { rules: [Validators.required], type: ETypeDependentInputs.widgetRequestSingleSelectDropdown },
      { rules: [Validators.required], type: ETypeDependentInputs.widgetRequestCategorySingleSelectDropdown2 },
      { rules: [Validators.required], type: ETypeDependentInputs.widgetRequestSingleSelectDropdown2 },
      { rules: [Validators.required], type: ETypeDependentInputs.arithmeticOperatorDropdown },
      { rules: [Validators.required], type: ETypeDependentInputs.ratioWidgetDisplayType },
      { type: 'filterSection' },
      { rules: [], type: ETypeDependentInputs.thresholdBasedColoringSelection },
    ],
  },
  [EWidget.sensorHealthStatus]: {
    component: SensorHealthStatusWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 23, w: 8 },
      [EDashboardLayout.fitToScreen]: { h: 3, w: 8 },
    },
    descriptionTranslateKey: 'widget.sensorHealthStatus.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [{ type: 'filterSection' }],
  },
  [EWidget.sqcdpMonthly]: {
    component: SqcdpMonthlyWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 36, w: 2 },
      [EDashboardLayout.fitToScreen]: { h: 5, w: 2 },
    },
    descriptionTranslateKey: 'widget.sqcdpMonthly.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['actionTracker'],
    visibleInputsInOrder: [
      { type: 'filterSection' },
      { rules: [Validators.maxLength(255)], type: ETypeDependentInputs.text },
      { rules: [Validators.required], type: ETypeDependentInputs.isDateShownSingleSelectDropdown },
      { rules: [Validators.required], type: ETypeDependentInputs.shiftSelectionDropdown },
      { rules: [], type: ETypeDependentInputs.excludeFutureDatesFromStatusColoringCheckbox },
      { rules: [], type: ETypeDependentInputs.endpointParameterMultiSelectDropdown },
    ],
  },
  [EWidget.tableView]: {
    component: TableViewWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 20, w: 4 },
      [EDashboardLayout.fitToScreen]: { h: 3, w: 4 },
    },
    descriptionTranslateKey: 'widget.tableView.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['utilityWidgets'],
    visibleInputsInOrder: [{ rules: [Validators.required], type: ETypeDependentInputs.tableMapping }],
  },
  [EWidget.kanbanView]: {
    component: KanbanViewWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 36, w: 2 },
      [EDashboardLayout.fitToScreen]: { h: 5, w: 2 },
    },
    descriptionTranslateKey: 'widget.kanbanView.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['actionTracker'],
    visibleInputsInOrder: [
      { type: 'filterSection' },
      {
        rules: [],
        type: ETypeDependentInputs.escalationStatusSingleSelectDropdown,
      },
      { rules: [], type: ETypeDependentInputs.endpointParameterMultiSelectDropdown },
    ],
  },
  [EWidget.temperatureAndHumiditySensorData]: {
    component: TemperatureAndHumiditySensorDataWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 12, w: 3 },
      [EDashboardLayout.fitToScreen]: { h: 2, w: 3 },
    },
    descriptionTranslateKey: 'widget.temperatureAndHumiditySensorData.description',
    refreshIntervalOptions: [60, 120, 180, 240, 300],
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [{ type: 'filterSection' }],
  },
  [EWidget.throughputTimePerformanceGantt]: {
    component: ThroughputTimePerformanceGanttWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 42, w: 2 },
      [EDashboardLayout.fitToScreen]: { h: 5, w: 2 },
    },
    descriptionTranslateKey: 'widget.throughputTimePerformanceGantt.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [{ type: 'filterSection' }],
  },
  [EWidget.throughputTimePerformanceKpi]: {
    component: ThroughputTimePerformanceKpiWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 42, w: 2 },
      [EDashboardLayout.fitToScreen]: { h: 5, w: 2 },
    },
    descriptionTranslateKey: 'widget.throughputTimePerformanceKpi.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [{ type: 'filterSection' }],
  },
  [EWidget.throughputTimePerformanceTable]: {
    component: ThroughputTimePerformanceTableWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 42, w: 2 },
      [EDashboardLayout.fitToScreen]: { h: 5, w: 2 },
    },
    descriptionTranslateKey: 'widget.throughputTimePerformanceTable.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [{ type: 'filterSection' }],
  },
  [EWidget.trendComparison]: {
    component: TrendComparisonWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 42, w: 4 },
      [EDashboardLayout.fitToScreen]: { h: 5, w: 4 },
    },
    descriptionTranslateKey: 'widget.trendComparison.description',
    refreshIntervalOptions: null,
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [
      { type: 'filterSection' },
      { rules: [Validators.required], type: ETypeDependentInputs.comparisonSourceSelectDropdown },
      { rules: [Validators.required], type: ETypeDependentInputs.fromLastNDays },
    ],
  },
  [EWidget.woCounts]: {
    component: WoCountsWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 18, w: 2 },
      [EDashboardLayout.fitToScreen]: { h: 3, w: 2 },
    },
    descriptionTranslateKey: 'widget.woCounts.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [{ type: 'filterSection' }],
  },
  [EWidget.woOEE]: {
    component: WoOeeWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 24, w: 7 },
      [EDashboardLayout.fitToScreen]: { h: 3, w: 7 },
    },
    descriptionTranslateKey: 'widget.woOEE.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [
      { type: 'filterSection' },
      { rules: [Validators.required], type: ETypeDependentInputs.oeeCalculationLevelSingleSelectDropdown },
      { rules: [Validators.required], type: ETypeDependentInputs.woOEEMetricLayoutMultiSelectDropdown },
      { rules: [Validators.required], type: ETypeDependentInputs.woOEEChartTypeSingleSelectDropdown },
    ],
  },
  [EWidget.comments]: {
    component: CommentsWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 42, w: 2 },
      [EDashboardLayout.fitToScreen]: { h: 5, w: 2 },
    },
    descriptionTranslateKey: 'widget.comments.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [{ type: 'filterSection' }],
  },
  [EWidget.woSpeed]: {
    component: WoSpeedWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 21, w: 2 },
      [EDashboardLayout.fitToScreen]: { h: 3, w: 2 },
    },
    descriptionTranslateKey: 'widget.woSpeed.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [{ type: 'filterSection' }],
  },
  [EWidget.imageViewer]: {
    component: ImageViewerWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 42, w: 2 },
      [EDashboardLayout.fitToScreen]: { h: 5, w: 2 },
    },
    descriptionTranslateKey: 'widget.imageViewer.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['utilityWidgets'],
    visibleInputsInOrder: [
      { rules: [Validators.required], type: ETypeDependentInputs.imageUpload },
      { rules: [Validators.required], type: ETypeDependentInputs.showThumbnailCheckbox },
      { rules: [], type: ETypeDependentInputs.fitImageToWidgetCheckbox },
      { rules: [Validators.max(3600), Validators.min(1)], type: ETypeDependentInputs.displayIntervalInput },
    ],
  },
  [EWidget.productionReview]: {
    component: ProductionReviewWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 31, w: 2 },
      [EDashboardLayout.fitToScreen]: { h: 4, w: 2 },
    },
    descriptionTranslateKey: 'widget.productionReview.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['oeeTracker'],
    visibleInputsInOrder: [{ type: 'filterSection' }],
  },
  [EWidget.categoryStatus]: {
    component: CategoryStatusWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 34, w: 5 },
      [EDashboardLayout.fitToScreen]: { h: 4, w: 5 },
    },
    descriptionTranslateKey: 'widget.categoryStatus.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['actionTracker'],
    visibleInputsInOrder: [
      { type: 'filterSection' },
      { rules: [Validators.required], type: ETypeDependentInputs.itemCategoryShiftConfigurations },
      { rules: [], type: ETypeDependentInputs.excludeFutureDatesFromStatusColoringCheckbox },
      { rules: [], type: ETypeDependentInputs.endpointParameterMultiSelectDropdown },
    ],
  },
  [EWidget.metabase]: {
    component: MetabaseWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 42, w: 2 },
      [EDashboardLayout.fitToScreen]: { h: 5, w: 2 },
    },
    descriptionTranslateKey: 'widget.metabase.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['utilityWidgets'],
    visibleInputsInOrder: [
      { rules: [Validators.required], type: ETypeDependentInputs.url },
      { rules: [], type: ETypeDependentInputs.endpointParameterMultiSelectDropdown },
    ],
  },
  [EWidget.workInstructions]: {
    component: WorkInstructionsWidgetComponent,
    defaultSize: {
      [EDashboardLayout.fixedHeight]: { h: 24, w: 3 },
      [EDashboardLayout.fitToScreen]: { h: 3, w: 3 },
    },
    descriptionTranslateKey: 'widget.workInstructions.description',
    refreshIntervalOptions: [-1, 0, 300, 900, 1800, 2700, 3600],
    relatedModules: ['oeeTracker', 'actionTracker'],
    visibleInputsInOrder: [{ type: 'filterSection' }],
  },
};

export const inputTypeDefaultImplementations = {
  [EWidgetInputType.boolean]: {
    extractFormControlValue: (value: any): boolean => Boolean(value),
    setFormField: (key: TCustomParameterKeyName, editDataConfiguration: TWidgetCustomParameters[EWidget]): boolean => {
      return key in editDataConfiguration && Boolean(editDataConfiguration[key as keyof typeof editDataConfiguration]);
    },
  },
  [EWidgetInputType.multiselectDropdown]: {
    extractFormControlValue: (value: any): any =>
      value ? (value as IDropdownOption[]).map((option) => option.id) : null,
    setFormField: (
      key: TCustomParameterKeyName,
      editDataConfiguration: TWidgetCustomParameters[EWidget],
    ): IDropdownOption<any>[] => {
      return key in editDataConfiguration
        ? (editDataConfiguration[key as keyof typeof editDataConfiguration] as unknown[]).map((id) => ({
            id,
            name: '...',
          }))
        : [];
    },
  },
  [EWidgetInputType.number]: {
    extractFormControlValue: (value: any): any => value,
    setFormField: (
      key: TCustomParameterKeyName,
      editDataConfiguration: TWidgetCustomParameters[EWidget],
    ): number | null => {
      return key in editDataConfiguration
        ? (editDataConfiguration[key as keyof typeof editDataConfiguration] as number)
        : null;
    },
  },

  [EWidgetInputType.singleSelectDropdown]: {
    extractFormControlValue: (value: any): any =>
      (value as IDropdownOption[])?.length ? (value as IDropdownOption[])[0].id : null,
    setFormField: (
      key: TCustomParameterKeyName,
      editDataConfiguration: TWidgetCustomParameters[EWidget],
    ): IDropdownOption<any>[] => {
      return key in editDataConfiguration
        ? [{ id: editDataConfiguration[key as keyof typeof editDataConfiguration], name: '...' }]
        : [];
    },
  },

  [EWidgetInputType.string]: {
    extractFormControlValue: (value: any): string | null => (typeof value === 'string' ? value.trim() || null : null),
    setFormField: (key: TCustomParameterKeyName, editDataConfiguration: TWidgetCustomParameters[EWidget]): string => {
      return key in editDataConfiguration ? editDataConfiguration[key as keyof typeof editDataConfiguration] : '';
    },
  },
  [EWidgetInputType.other]: {
    extractFormControlValue: (value: any): any => value,
    setFormField: (key: TCustomParameterKeyName, editDataConfiguration: TWidgetCustomParameters[EWidget]): any => {
      return key in editDataConfiguration ? editDataConfiguration[key as keyof typeof editDataConfiguration] : null;
    },
  },
};

export const addEditWidgetInputConfiguration: { [Key in ETypeDependentInputs]: ITypeBasedConfigurationInput<Key> } = {
  [ETypeDependentInputs.arithmeticOperatorDropdown]: {
    editDataKeyName: () => 'operator',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.comparisonSourceSelectDropdown]: {
    editDataKeyName: () => 'dataSource',
    rules: [Validators.maxLength(1)],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.datatableConfiguration]: {
    editDataKeyName: () => 'datatableConfiguration',
    rules: [],
    type: EWidgetInputType.other,
  },
  [ETypeDependentInputs.displayIntervalInput]: {
    editDataKeyName: () => 'displayInterval',
    rules: [],
    type: EWidgetInputType.number,
  },
  [ETypeDependentInputs.formAssociation]: {
    editDataKeyName: () => 'formAssociation',
    rules: [],
    type: EWidgetInputType.other,
  },
  [ETypeDependentInputs.formSingleSelectDropdown]: {
    editDataKeyName: () => 'formId',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.formAutofillSingleSelectDropdown]: {
    editDataKeyName: () => 'formAutofillId',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.fromLastNDays]: {
    editDataFormResetCustomImplementation: (editDataConfiguration) => {
      return 'fromLastNDays' in editDataConfiguration ? editDataConfiguration.fromLastNDays : 3;
    },
    editDataKeyName: () => 'fromLastNDays',
    rules: [Validators.min(1), Validators.max(30)],
    type: EWidgetInputType.number,
  },
  [ETypeDependentInputs.isDateShownSingleSelectDropdown]: {
    editDataKeyName: () => 'isDateShown',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.shiftSelectionDropdown]: {
    editDataKeyName: () => 'shifts',
    rules: [],
    type: EWidgetInputType.other,
  },
  [ETypeDependentInputs.laborEfficiencyModeDropdown]: {
    editDataKeyName: () => 'laborHourMode',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.laborEfficiencyUnitDropdown]: {
    editDataKeyName: () => 'laborHourUnit',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.nextFiveWorkOrderCountSingleSelectDropdown]: {
    editDataKeyName: () => 'nextFiveWorkOrderCount',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.nextFiveWorkOrderListFieldFourSingleSelectDropdown]: {
    editDataKeyName: () => 'listFieldFour',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.nextFiveWorkOrderListFieldOneSingleSelectDropdown]: {
    editDataKeyName: () => 'listFieldOne',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.nextFiveWorkOrderListFieldThreeSingleSelectDropdown]: {
    editDataKeyName: () => 'listFieldThree',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.nextFiveWorkOrderListFieldTwoSingleSelectDropdown]: {
    editDataKeyName: () => 'listFieldTwo',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.ratioWidgetDisplayType]: {
    editDataKeyName: () => 'displayType',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.fitImageToWidgetCheckbox]: {
    editDataKeyName: () => 'fitImageToWidget',
    rules: [],
    type: EWidgetInputType.boolean,
  },
  [ETypeDependentInputs.showThumbnailCheckbox]: {
    editDataKeyName: () => 'showThumbnail',
    rules: [],
    type: EWidgetInputType.boolean,
  },
  [ETypeDependentInputs.text]: {
    editDataKeyName: (widget) => {
      // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
      switch (widget) {
        case EWidget.dfQrCode:
          return 'qrPassKey';
        case EWidget.navigation:
          return 'middleTitle';
        case EWidget.note:
          return 'note';
        case EWidget.sqcdpMonthly:
          return 'hyperlinkUrl';
        case EWidget.qrCode:
        default:
          return 'text';
      }
    },
    rules: [],
    type: EWidgetInputType.string,
  },
  [ETypeDependentInputs.text2]: {
    editDataKeyName: (widget) => {
      // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
      switch (widget) {
        case EWidget.navigation:
        default:
          return 'hyperlinkUrl';
      }
    },
    rules: [Validators.maxLength(255)],
    type: EWidgetInputType.string,
  },
  [ETypeDependentInputs.url]: {
    editDataKeyName: () => 'url',
    extractFormControlValueCustomImplementation: (value: string): string | null => {
      if (!value) {
        return null;
      }

      const cleanInput = value.trim();

      if (cleanInput.startsWith('<')) {
        const doc = new DOMParser().parseFromString(cleanInput, 'text/html');
        const iframe = doc.querySelector('iframe');

        return iframe?.getAttribute('src') ?? null;
      }

      try {
        return new URL(cleanInput).href;
      } catch {
        return cleanInput;
      }
    },
    rules: [],
    type: EWidgetInputType.string,
  },
  [ETypeDependentInputs.widgetRequestSingleSelectDropdown]: {
    editDataKeyName: () => 'widgetRequest',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.widgetRequestSingleSelectDropdown2]: {
    editDataKeyName: () => 'widgetRequest2',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.widgetRequestCategorySingleSelectDropdown]: {
    editDataKeyName: () => 'widgetRequestCategory',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.widgetRequestCategorySingleSelectDropdown2]: {
    editDataKeyName: () => 'widgetRequest2Category',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.woOEEChartTypeSingleSelectDropdown]: {
    editDataKeyName: () => 'woOEEChartType',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.oeeCalculationLevelSingleSelectDropdown]: {
    editDataKeyName: () => 'oeeCalculationLevel',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.checkInLogsGroupingSingleSelectDropdown]: {
    editDataKeyName: () => 'grouping',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.checkInLogsDisplayConfigurationMultiSelectDropdown]: {
    editDataKeyName: () => 'checkInDisplayConfiguration',
    rules: [],
    type: EWidgetInputType.multiselectDropdown,
  },
  [ETypeDependentInputs.woOEEMetricLayoutMultiSelectDropdown]: {
    editDataKeyName: () => 'woOEEMetricLayout',
    rules: [],
    type: EWidgetInputType.multiselectDropdown,
  },
  [ETypeDependentInputs.thresholdBasedColoringSelection]: {
    editDataKeyName: () => 'thresholdBasedColoring',
    rules: [],
    type: EWidgetInputType.other,
  },
  [ETypeDependentInputs.imageUpload]: {
    editDataKeyName: () => 'imageUpload',
    rules: [],
    type: EWidgetInputType.other,
  },
  [ETypeDependentInputs.escalationStatusSingleSelectDropdown]: {
    editDataKeyName: () => 'escalationStatus',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.formDataType]: {
    editDataKeyName: () => 'formDataType',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.resultAggregation]: {
    editDataKeyName: () => 'resultAggregation',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.formField]: {
    editDataKeyName: () => 'formField',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.tableMapping]: {
    editDataKeyName: () => 'mappingId',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
  [ETypeDependentInputs.itemCategoryShiftConfigurations]: {
    editDataKeyName: () => 'itemCategoryShiftConfigurations',
    rules: [],
    type: EWidgetInputType.other,
  },
  [ETypeDependentInputs.excludeFutureDatesFromStatusColoringCheckbox]: {
    editDataKeyName: () => 'excludeFutureDatesFromStatusColoring',
    rules: [],
    type: EWidgetInputType.boolean,
  },
  [ETypeDependentInputs.endpointParameterMultiSelectDropdown]: {
    editDataKeyName: () => 'endpointParameters',
    rules: [],
    type: EWidgetInputType.multiselectDropdown,
  },
  [ETypeDependentInputs.sortBySingleSelectDropdown]: {
    editDataKeyName: () => 'sortBy',
    rules: [],
    type: EWidgetInputType.singleSelectDropdown,
  },
};

export const sqcdpWidgetNames: readonly EWidget[] = [
  EWidget.actionTrackerIssues,
  EWidget.actionTrackerCard,
  EWidget.sqcdpMonthly,
] as const;
